import { Button, ButtonSize, Shape, Tooltip } from '@axieinfinity/matcha'
import { CopySimpleIcon } from '@axieinfinity/matcha-icons'

import { Box } from '~/components/Box'
import { Input } from '~/components/Input'
import type { TPasswordModalProps } from '~/components/Modals/PasswordModal'
import { NiceModal } from '~/components/NiceModal'
import { ModalKey } from '~/constants/modal'
import { ModeAction } from '~/constants/mode'
import { useAuthContext } from '~/hooks/useAuthContext'
import { useWrappedToast } from '~/hooks/useWrappedToast'

import { useEmailSignIn } from './useEmailSignIn'

export const EmailSignIn: React.FC = () => {
  const { user, userV2 } = useAuthContext()
  const { showInfoMessage } = useWrappedToast()
  const { modePassword, modeEmail, onSetupEmail, onChangeEmail } =
    useEmailSignIn()

  const mpcAddress = userV2?.account?.wallet?.identity

  const onCopyUserId = () => {
    if (user?.email) {
      navigator.clipboard.writeText(user?.email)
      showInfoMessage('Address copied to clipboard')
    }
  }

  return (
    <Box className="flex flex-col gap-12 md:flex-row md:justify-between md:gap-48 p-24">
      <div className="w-full md:w-1/2">
        <div className="text-h6 mb-4">Email Sign-In</div>
        <div className="text-body-s text-text-color-subdued">
          Regularly update your password to prevent unauthorized access to your
          account.
        </div>
      </div>
      <div className="flex-1 flex flex-col gap-16">
        {modeEmail === ModeAction.SETUP ? (
          <Button
            className="w-fit self-end justify-center"
            text="Set up email"
            shape={Shape.Default}
            size={ButtonSize.Small}
            onClick={onSetupEmail}
          />
        ) : (
          <>
            <Input
              shape={Shape.Default}
              suffixIcon={<CopySimpleIcon onClick={onCopyUserId} />}
              readOnly
              value={user?.email}
            />
            <div className="flex flex-col sm:flex-row gap-8 sm:justify-end items-end">
              <Button
                className="w-fit"
                text={
                  modePassword === ModeAction.CHANGE
                    ? 'Change password'
                    : 'Set up password'
                }
                onClick={() =>
                  NiceModal.show<TPasswordModalProps>(ModalKey.PASSWORD, {
                    mode: modePassword,
                  })
                }
                type="button"
                shape={Shape.Default}
                size={ButtonSize.Small}
              />

              {mpcAddress || modePassword === ModeAction.SETUP ? (
                <Tooltip
                  id="change-email"
                  target={
                    <Button
                      className="w-fit"
                      disabled
                      text="Change email"
                      type="button"
                      shape={Shape.Default}
                      size={ButtonSize.Small}
                    />
                  }
                  place="top"
                >
                  <p className="max-w-[360px] text-center">
                    {modePassword === ModeAction.SETUP
                      ? 'You need to setup password before'
                      : 'This feature is temporarily not available for wallets created with Sky Mavis account'}
                  </p>
                </Tooltip>
              ) : (
                <Button
                  className="w-fit"
                  text="Change email"
                  type="button"
                  onClick={onChangeEmail}
                  shape={Shape.Default}
                  size={ButtonSize.Small}
                />
              )}
            </div>
          </>
        )}
      </div>
    </Box>
  )
}
